import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, useHistory } from 'react-router-dom';
import SideNavigation from './SideNavigation';
import BreadCrumb from './BreadCrumb';
import StreamGuide from './StreamGuide';
import axios from 'axios';
import LeagueLanding from './LeagueLanding';
import LeagueContent from './LeagueContent';
import { useSelector } from 'react-redux';
import Loading from '../../components/Loading';
import { withMixpanel } from 'Utils/mixpanel';
import { isWebviewMode } from 'Utils/webviewMode';
import EnvironmentManager from '../../../utils/environmentManager';

let leagueInsightsData = null;
let isFetching = false;
let dataPromise = null;
const WORLD_CUP_2026_TOURNAMENT_ID = 79;
const TOURNAMENT_BRACKET_WIDGET_SLUG = 'eslesmeler';
const TOURNAMENT_BRACKET_WIDGET = {
  name: 'Eşleşmeler',
  title: 'Eşleşmeler',
  slug: TOURNAMENT_BRACKET_WIDGET_SLUG,
  isTournamentBracket: EnvironmentManager.getMessageText({ key: 'tournament_bracket_visible' }) === 'true',
  meta: {
    title: '2026 FIFA Dünya Kupası Eşleşmeler - Bilyoner',
    description: "2026 FIFA Dünya Kupası eşleşmeleri Bilyoner'de! Turnuva ağacını takip ederek mücadeleyi anbean izlemek için hemen tıklayın.",
  },
};

const getLastSlugSegment = slug => {
  if (typeof slug !== 'string' || !slug.trim()) return '';
  return slug.replace(/\/+$/g, '').split('/').filter(Boolean).pop()?.toLowerCase() || '';
};

const addTournamentBracketWidgetToLeague = league => {
  if (
    !TOURNAMENT_BRACKET_WIDGET.isTournamentBracket
    || league?.tournamentId !== WORLD_CUP_2026_TOURNAMENT_ID
    || !Array.isArray(league?.widgets)
  ) {
    return league;
  }

  const alreadyExists = league.widgets.some(widget => getLastSlugSegment(widget?.slug) === TOURNAMENT_BRACKET_WIDGET_SLUG);

  if (alreadyExists) {
    return league;
  }

  const widgets = [...league.widgets];
  const standingsIndex = widgets.findIndex(widget =>
    (getLastSlugSegment(widget?.slug) === 'puan-durumu')
      || ((widget?.name || '').toLowerCase() === 'puan durumu')
  );

  if (standingsIndex === -1) {
    const maxOrder = widgets.reduce((max, widget) => Math.max(max, Number(widget?.order) || 0), 0);
    widgets.push({ ...TOURNAMENT_BRACKET_WIDGET, order: maxOrder + 1 });
  } else {
    const previousOrder = Number(widgets[standingsIndex]?.order) || standingsIndex + 1;
    widgets.splice(standingsIndex + 1, 0, { ...TOURNAMENT_BRACKET_WIDGET, order: previousOrder + 0.1 });
  }

  return { ...league, widgets };
};

const enrichLeagueInsightsResponse = response => {
  if (!response || !Array.isArray(response.leagues)) {
    return response;
  }

  return {
    ...response,
    leagues: response.leagues.map(category => {
      if (!Array.isArray(category?.leagues)) {
        return category;
      }

      return {
        ...category,
        leagues: category.leagues.map(addTournamentBracketWidgetToLeague),
      };
    }),
  };
};

const getLeagueInsights = () => {
  if (leagueInsightsData) {
    return Promise.resolve(leagueInsightsData);
  }
  if (dataPromise) {
    return dataPromise;
  }
  if (isFetching) {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        if (leagueInsightsData) {
          clearInterval(checkInterval);
          resolve(leagueInsightsData);
        }
      }, 50);
    });
  }
  isFetching = true;
  // eslint-disable-next-line no-undef
  dataPromise = axios.get(`${API_GATEWAY}/mobile/match-card/league-insights`)
    .then(res => {
      leagueInsightsData = enrichLeagueInsightsResponse(res.data);
      isFetching = false;
      return leagueInsightsData;
    })
    .catch(err => {
      isFetching = false;
      dataPromise = null;
      throw err;
    });
  return dataPromise;
};

const LeagueCenter = ({ isNotFoundPage, mixpanel }) => {
  const [data, setData] = useState(leagueInsightsData);
  const [loading, setLoading] = useState(!leagueInsightsData);
  const initialRenderDone = useRef(false);
  const userAgent = useSelector(state => state.userAgent);
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const history = useHistory();
  const isWebView = isWebviewMode(history?.location?.search);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isSideNavOpen && userAgent?.device?.type === 'mobile') {
      document.body.classList.add('body--no-scroll');
    } else {
      document.body.classList.remove('body--no-scroll');
    }
    return () => document.body.classList.remove('body--no-scroll');
  }, [isSideNavOpen, userAgent?.device?.type]);

  useEffect(() => {
    if (initialRenderDone.current) return;
    initialRenderDone.current = true;
    getLeagueInsights()
      .then(data => {
        setData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (isNotFoundPage) {
    return <div>Sayfa bulunamadı!</div>;
  }
  if (loading) {
    return <div className="league-layout league-center"><Loading /></div>;
  }
  if (!data) return null;

  return (
    <div className="league-layout league-center">
      { userAgent?.device?.type === 'desktop' && <BreadCrumb data={ data }/> }
      <div className={ `league-layout-content ${isWebView ? 'webview' : 'desktopview'}` }>
        <SideNavigation data={ data } isSideNavOpen={ isSideNavOpen } setIsSideNavOpen={ setIsSideNavOpen } />
        <div className="content-wrapper">
          <Switch>
            <Route exact path="/lig-merkezi/canli-yayin-rehberi" render={ () => <StreamGuide data={ data }  isSideNavOpen={ isSideNavOpen } setIsSideNavOpen={ setIsSideNavOpen }/> }/>
            <Route exact path="/lig-merkezi/:mainSlug/:tabSlug?"
              render={ props => <LeagueContent { ...props } data={ data }  isSideNavOpen={ isSideNavOpen } setIsSideNavOpen={ setIsSideNavOpen }/> }/>
            <Route exact path="/lig-merkezi" render={ () => <LeagueLanding data={ data } isSideNavOpen={ isSideNavOpen } setIsSideNavOpen={ setIsSideNavOpen } /> }/>
          </Switch>
        </div>
      </div>
    </div>
  );
};

LeagueCenter.propTypes = {
  isNotFoundPage: PropTypes.bool,
  mixpanel: PropTypes.object,
};

export default withMixpanel(LeagueCenter);
